import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { i18n } from '@/plugins/i18n'
import { DEFAULT_LOCALE, PREFIX_LOCALES, SUPPORTED_LOCALES, type Locale, isLocale } from '@/router/locales'
import { normalizeLocale, getFallbackLocale } from '@/utils/locale'
import { lsGet, lsSet } from '@/utils/storage'

function isPrefixLocale(value: string): value is Exclude<Locale, typeof DEFAULT_LOCALE> {
  return (PREFIX_LOCALES as readonly string[]).includes(value)
}

function getRouteLocale(): Locale | null {
  if (typeof window === 'undefined') return null

  const routeLocale = normalizeLocale(window.location.pathname.split('/')[1] ?? '')

  if (isPrefixLocale(routeLocale)) {
    return routeLocale
  }

  return DEFAULT_LOCALE
}

export const useLocaleStore = defineStore('locale', () => {
  const fallback = getFallbackLocale()
  const currentLocale = ref<Locale>(fallback)

  const availableLocales = computed(() => SUPPORTED_LOCALES)

  function setLocale(locale: Locale) {
    if (!isLocale(locale)) {
      locale = fallback
    }

    if (currentLocale.value === locale) return

    currentLocale.value = locale
    i18n.global.locale.value = locale
    lsSet('locale', normalizeLocale(locale))
  }

  function initLocale() {
    const routeLocale = getRouteLocale()

    if (routeLocale) {
      setLocale(routeLocale)
      return
    }

    const saved = normalizeLocale(lsGet('locale'))
    const defaultLocale =
      saved && isLocale(saved) ? saved : fallback

    setLocale(defaultLocale)
  }

  return { currentLocale, availableLocales, setLocale, initLocale }
})
